import { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box } from "@mui/material";
import { useTerminology } from "../../Core/useTerminology";
import {
  FilterAlt,
  Search,
  History,
  CloudUpload,
  UploadFile,
  OpenInBrowser,
  CloudSync,
} from "@mui/icons-material";
import {
  useSingleCompany,
  useUpdateAboutCompany,
  useDeleteSingleCompany,
  useUpdateSingleCompany,
} from "../../Query/useCompanies";
import {
  useDeleteEvaluation,
  useDownloadEvaluationDocument,
} from "../../Query/useEvaluation";
import { useMicrosoftStatus } from "../../Query/useSharepoint";
import { downloadDocumentBlob } from "../../Helpers/documentHelper";
import WebsiteUpload from "../WebsiteUpload/WebsiteUpload";
import SharepointSync from "../SharepointSync/SharepointSync";
import FileUploadModal from "../FileUpload/FileUploadModal";
import CoverSheetUploadModal from "../CoverSheetUploadModal/CoverSheetUploadModal";
import ConfirmationDialog from "../Common/ConfirmationDialog";
import {
  EvaluationStatus,
  Features,
  UserRole,
} from "../../Enums/companyStatus";
import { EvaluationResponse } from "../../Models/evaluation";
import { useSingleEvaluation } from "../../Query/useEvaluation";
import Loading from "../Common/Loading";
import { useWorkspaceColors } from "../../Contexts/WorkspaceColorContext";
import SmartSearch from "./SmartSearch";
import EvaluationHistory from "./EvaluationHistory";
import FilteredContent from "./FilteredContent";
import { EditCompanyFormData, EditCompanyModal } from "./EditCompanyModal";
import { CompanyInfoCard } from "./CompanyInfoCard";
import { ActionChipsGrid } from "./ActionChipsGrid";
import { InProgressEvaluationsCard } from "./InProgressEvaluationsCard";
import { ComplianceEvaluationsCard } from "./ComplianceEvaluationsCard";
import { NoEvaluationsCard } from "./NoEvaluationsCard";
import colors from "../../Constants/colors";
import color from "../../Constants/colors";
import { hasRole, hasPermission } from "../../Helpers/authHelpers";

export default function EntityComponent() {
  const { companyId } = useParams<{ companyId: string }>();
  const { colors: workspace } = useWorkspaceColors();
  const { t } = useTranslation("singleCompanyComponent");
  const terminology = useTerminology();
  const navigate = useNavigate();

  const { data: companyInfo, isLoading: isCompanyLoading } =
    useSingleCompany(companyId);
  const { data: microsoftStatus } = useMicrosoftStatus();
  const { mutate: updateAboutCompany } = useUpdateAboutCompany();
  const { mutate: deleteEvaluationMutate } = useDeleteEvaluation(
    companyId || "",
  );
  const { mutate: downloadEvaluationDocument } =
    useDownloadEvaluationDocument();
  const { mutate: deleteSingleCompanyMutate } = useDeleteSingleCompany();
  const { mutate: updateSingleCompanyMutate } = useUpdateSingleCompany();

  const inProgressStatusesSet = useMemo(
    () =>
      new Set([
        EvaluationStatus.EVALUATION_IN_PROGRESS,
        EvaluationStatus.GATHERING_DIGITAL_PERFORMANCE,
        EvaluationStatus.FETCHING_COMPANY_DETAILS,
        EvaluationStatus.PROCESSING_COMPANY_INFORMATIONS,
        EvaluationStatus.GRADING_COMPANY,
        EvaluationStatus.INITIALIZED,
        EvaluationStatus.PAUSED,
      ]),
    [],
  );

  const inProgressEvaluations = companyInfo?.evaluations.filter((evaluation) =>
    inProgressStatusesSet.has(evaluation.status),
  );

  const evaluations = useMemo(
    () =>
      companyInfo?.evaluations.filter(
        (evaluation) => !inProgressStatusesSet.has(evaluation.status),
      ) || [],
    [companyInfo?.evaluations, inProgressStatusesSet],
  );

  const [selectedEvaluation, setSelectedEvaluation] =
    useState<EvaluationResponse>();

  useEffect(() => {
    if (evaluations.length > 0 && !selectedEvaluation) {
      setSelectedEvaluation(evaluations[0]);
    }
  }, [evaluations, selectedEvaluation]);

  const {
    data: selectedEvaluationData,
    isLoading: isLoadingSelectedEvaluation,
  } = useSingleEvaluation(
    selectedEvaluation?.evaluation_id || evaluations[0]?.evaluation_id,
  );

  const [openFilteredContent, setOpenFilteredContent] = useState(false);
  const [openSmartSearch, setOpenSmartSearch] = useState(false);
  const [openEvaluationHistory, setOpenEvaluationHistory] = useState(false);
  const [openFileManagement, setOpenFileManagement] = useState(false);
  const [openCoverSheet, setOpenCoverSheet] = useState(false);
  const [openWebsiteManagement, setOpenWebsiteManagement] = useState(false);
  const [openSharepointSync, setOpenSharepointSync] = useState(false);
  const [deleteEvaluationId, setDeleteEvaluationId] = useState<number | null>(
    null,
  );
  const [showDeleteCompanyDialog, setShowDeleteCompanyDialog] = useState(false);
  const [openEditCompany, setOpenEditCompany] = useState(false);

  const handleFilteredContentOpen = () => setOpenFilteredContent(true);
  const handleFilteredContentClose = () => setOpenFilteredContent(false);

  const handleSmartSearchOpen = () => setOpenSmartSearch(true);
  const handleSmartSearchClose = () => setOpenSmartSearch(false);

  const handleEvaluationHistoryOpen = () => setOpenEvaluationHistory(true);
  const handleEvaluationHistoryClose = () => setOpenEvaluationHistory(false);

  const handleFileManagementOpen = () => setOpenFileManagement(true);
  const handleFileManagementClose = () => setOpenFileManagement(false);

  const handleCoverSheetOpen = () => setOpenCoverSheet(true);
  const handleCoverSheetClose = () => setOpenCoverSheet(false);

  const handleWebsiteManagementOpen = () => setOpenWebsiteManagement(true);
  const handleWebsiteManagementClose = () => setOpenWebsiteManagement(false);

  const handleSharepointSyncOpen = () => setOpenSharepointSync(true);
  const handleSharepointSyncClose = () => setOpenSharepointSync(false);

  const actionChipsWithHandlers = [
    {
      label: t("companyActions.filteredContent"),
      icon: FilterAlt,
      action: handleFilteredContentOpen,
    },
    {
      label: t("companyActions.smartSearch.smartSearchTitle"),
      icon: Search,
      action: handleSmartSearchOpen,
    },
    {
      label: t("companyActions.evaluationHistory"),
      icon: History,
      action: handleEvaluationHistoryOpen,
    },
    {
      label: t("companyActions.fileManagement"),
      icon: CloudUpload,
      action: handleFileManagementOpen,
    },
    ...(hasRole(UserRole.ADMIN) || hasPermission(Features.QA_INSIGHTS)
      ? [
          {
            label: t("companyActions.coverSheet"),
            icon: UploadFile,
            action: handleCoverSheetOpen,
          },
        ]
      : []),
    {
      label: t("companyActions.websiteManagement"),
      icon: OpenInBrowser,
      action: handleWebsiteManagementOpen,
    },
    {
      label: t("companyActions.sharepointSync"),
      icon: CloudSync,
      action: handleSharepointSyncOpen,
      disabled: !microsoftStatus?.connected || microsoftStatus?.revoked,
    },
  ];

  const handleSelectEvaluation = (evaluationId: number) => {
    setSelectedEvaluation(
      evaluations.find((e) => e.evaluation_id === evaluationId)!,
    );
  };

  const handleOpenInProgressEvaluation = (evaluationId: number) => {
    window.open(`/evaluation_in_progress/${evaluationId}`, "_blank");
  };

  const handleDownloadEvaluation = (evaluationId: number) => {
    const evaluation = evaluations.find(
      (e) => e.evaluation_id === evaluationId,
    );
    downloadEvaluationDocument(
      { evaluationId },
      {
        onSuccess: (response) => {
          downloadDocumentBlob(
            response,
            `${evaluation?.question_list_name || "evaluation"}.docx`,
          );
        },
      },
    );
  };

  const handleDeleteEvaluation = (evaluationId: number) => {
    setDeleteEvaluationId(evaluationId);
  };

  const handleConfirmDeleteEvaluation = (confirmed: boolean) => {
    if (confirmed && deleteEvaluationId !== null) {
      deleteEvaluationMutate(String(deleteEvaluationId));
    }
    setDeleteEvaluationId(null);
  };

  const handleCompanyEdit = () => {
    setOpenEditCompany(true);
  };

  const handleEditCompanyClose = () => {
    setOpenEditCompany(false);
  };

  const handleEditCompanySave = (data: EditCompanyFormData) => {
    if (!companyId || !companyInfo) return;

    updateSingleCompanyMutate(
      {
        id: companyId,
        name: data.name,
        about: data.about,
        location: data.location,
        industry: data.industry,
        icon_id: data.icon_id,
      },
      {
        onSuccess: () => {
          setOpenEditCompany(false);
        },
      },
    );
  };

  const handleCompanyDelete = () => {
    setShowDeleteCompanyDialog(true);
  };

  const handleConfirmDeleteCompany = (confirmed: boolean) => {
    if (confirmed && companyId) {
      deleteSingleCompanyMutate(companyId, {
        onSuccess: () => {
          navigate("/entity");
        },
      });
    }
    setShowDeleteCompanyDialog(false);
  };

  const handleSaveFeedback = (
    sectionId: string,
    responses: Record<string, string>,
  ) => {
    updateAboutCompany({
      companyId: companyId || "",
      evaluationId: selectedEvaluation?.evaluation_id.toString() || "",
      answers: {
        [sectionId]: responses,
      },
    });
  };

  if (isCompanyLoading || !companyInfo) {
    return (
      <Loading
        backgroundColor={color.gray100}
        color1={workspace.accent}
        color2={workspace.primary_dark}
      />
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: colors.gray50 }}>
      <Box sx={{ maxWidth: "1280px", mx: "auto", px: 3, py: 4 }}>
        <CompanyInfoCard
          company={companyInfo}
          onEdit={handleCompanyEdit}
          onDelete={handleCompanyDelete}
        />

        <ActionChipsGrid chips={actionChipsWithHandlers} />

        {inProgressEvaluations && inProgressEvaluations.length > 0 && (
          <InProgressEvaluationsCard
            evaluations={inProgressEvaluations}
            onOpenEvaluation={handleOpenInProgressEvaluation}
          />
        )}

        {evaluations.length === 0 && (
          <NoEvaluationsCard companyId={companyId} />
        )}

        {evaluations.length > 0 && (
          <ComplianceEvaluationsCard
            evaluations={evaluations}
            isLoadingSelectedEvaluation={isLoadingSelectedEvaluation}
            selectedEvaluation={selectedEvaluationData}
            onSelectEvaluation={handleSelectEvaluation}
            onDownloadEvaluation={handleDownloadEvaluation}
            onDeleteEvaluation={handleDeleteEvaluation}
            onSaveFeedback={handleSaveFeedback}
          />
        )}

        <FilteredContent
          open={openFilteredContent}
          onClose={handleFilteredContentClose}
          companyInfo={companyInfo}
        />
        <SmartSearch
          key={openSmartSearch ? "smOpen" : "smClosed"}
          open={openSmartSearch}
          onClose={handleSmartSearchClose}
        />
        <EvaluationHistory
          companyId={companyId || ""}
          open={openEvaluationHistory}
          onClose={handleEvaluationHistoryClose}
        />
        <FileUploadModal
          open={openFileManagement}
          onClose={handleFileManagementClose}
          documents={companyInfo.documents}
        />
        <CoverSheetUploadModal
          open={openCoverSheet}
          onClose={handleCoverSheetClose}
          documents={companyInfo.documents}
        />
        <WebsiteUpload
          companyId={companyId || ""}
          open={openWebsiteManagement}
          onClose={handleWebsiteManagementClose}
        />
        <SharepointSync
          companyId={companyId || ""}
          open={openSharepointSync}
          onClose={handleSharepointSyncClose}
        />
        <EditCompanyModal
          key={openEditCompany ? "ecOpen" : "ecClosed"}
          open={openEditCompany}
          onClose={handleEditCompanyClose}
          company={companyInfo}
          onSave={handleEditCompanySave}
        />

        <ConfirmationDialog
          open={deleteEvaluationId !== null}
          onClose={handleConfirmDeleteEvaluation}
          title={t(
            "deleteEvaluationConfirmationDialog.deleteEvaluationConfirmation",
          )}
          description={t(
            "deleteEvaluationConfirmationDialog.areYouSureDeleteEvaluation",
          )}
          content={t(
            "deleteEvaluationConfirmationDialog.areYouSureDeleteEvaluation",
          )}
          actions={[
            {
              label: t("deleteEvaluationConfirmationDialog.no"),
              onClick: () => handleConfirmDeleteEvaluation(false),
            },
            {
              label: t("deleteEvaluationConfirmationDialog.yes"),
              onClick: () => handleConfirmDeleteEvaluation(true),
            },
          ]}
        />

        <ConfirmationDialog
          open={showDeleteCompanyDialog}
          onClose={handleConfirmDeleteCompany}
          title={t("deleteConfirmationDialog.deleteCompanyConfirmation", {
            entity: terminology.singular,
          })}
          description={t("deleteConfirmationDialog.areYouSureDeleteCompany", {
            entity: terminology.singular,
          })}
          content={t("deleteConfirmationDialog.areYouSureDeleteCompany", {
            entity: terminology.singular,
          })}
          actions={[
            {
              label: t("deleteConfirmationDialog.no"),
              onClick: () => handleConfirmDeleteCompany(false),
            },
            {
              label: t("deleteConfirmationDialog.yes"),
              onClick: () => handleConfirmDeleteCompany(true),
            },
          ]}
        />
      </Box>
    </Box>
  );
}
